// INVLOC1 Slice 5 (docs/build-briefs/INVLOC1-S5-preferred-vendor-and-account-per-bar.md section 3.8; DEC-326). The pure
// part of the two per-location tables: the item editor's "Preferred vendor by location" and the vendor card's "Account number by
// location". Same pattern as the Slice 2 par table (stockSettingsModel.ts): shown only when the server says the person may
// plan for MORE THAN ONE location, one Save, and a Save sends only the locations that changed. With one location nothing is shown,
// because the existing "Preferred" toggle on the offer IS that location's choice.

import type { PreferredOfferLocationDto, VendorLocationAccountDto } from "./api.ts";

/** The table is worth showing only when there is more than one location to choose for. */
export function locationChoicesVisible(locations: readonly { locationId: string }[]): boolean {
  return locationCountShowsChoices(locations.length);
}

/** The same rule from a bare count (the vendor list knows only how many locations the person may plan for). */
export function locationCountShowsChoices(count: number): boolean {
  return count > 1;
}

/** Value of the "franchisee default" option in a location's vendor picker. */
export const DEFAULT_CHOICE_VALUE = "";

export const PREFERRED_BY_LOCATION_COPY = {
  heading: "Preferred vendor by location",
  intro: "Pick where each location orders this item. Default uses the Preferred vendor above.",
  locationHeader: "Location",
  vendorHeader: "Orders from",
  thisTabletTag: "This device",
  save: "Save preferred vendors",
  saved: "Saved. Each location's next cart and suggested order use its own vendor.",
  savesRightAway: "Saves right away, separate from Save item.",
  viewOnly: "View only. Ask a manager to change where a location orders.",
  nothingToSave: "Nothing changed.",
} as const;

export const ACCOUNTS_BY_LOCATION_COPY = {
  heading: "Account number by location",
  intro:
    "The account number each location holds with this vendor. It prints on that location's purchase orders. A location left blank uses the account reference above.",
  locationHeader: "Location",
  accountHeader: "Account number",
  placeholder: "Uses vendor-wide number",
  onFile: "On file",
  thisTabletTag: "This device",
  save: "Save account numbers",
  saved: "Saved. Each location's purchase orders print its own account number.",
  savesRightAway: "Saves right away.",
  viewOnly: "View only. Ask a manager to change account numbers.",
  nothingToSave: "Nothing changed.",
} as const;

/** "Default: Acme Beverage" or, when no offer is the default, "Default: none set". Short, so it fits the picker. */
export function defaultOptionLabel(defaultVendorLabel: string | null): string {
  return `Default: ${defaultVendorLabel ?? "none set"}`;
}

/** The entries one Save sends: only the locations whose picked value differs from what was loaded. */
export function buildPreferredEntries(
  loaded: readonly PreferredOfferLocationDto[],
  picked: ReadonlyMap<string, string>,
): Array<{ locationId: string; vendorItemOfferId: string | null }> {
  const entries: Array<{ locationId: string; vendorItemOfferId: string | null }> = [];
  for (const siteLocation of loaded) {
    const value = picked.get(siteLocation.locationId);
    if (value === undefined) continue;
    const next = value === DEFAULT_CHOICE_VALUE ? null : value;
    if (next !== siteLocation.chosenOfferId) {
      entries.push({ locationId: siteLocation.locationId, vendorItemOfferId: next });
    }
  }
  return entries;
}

/** The entries one Save sends: only the locations whose typed number differs from what was loaded (a blank clears). */
export function buildAccountEntries(
  loaded: readonly VendorLocationAccountDto[],
  typed: ReadonlyMap<string, string>,
): Array<{ locationId: string; accountReference: string | null }> {
  const entries: Array<{ locationId: string; accountReference: string | null }> = [];
  for (const siteLocation of loaded) {
    const value = typed.get(siteLocation.locationId);
    if (value === undefined) continue;
    const next = value.trim() === "" ? null : value.trim();
    if (next !== siteLocation.accountReference) {
      entries.push({ locationId: siteLocation.locationId, accountReference: next });
    }
  }
  return entries;
}
